import {
  GridFilterItem,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {useState, useCallback, useEffect} from 'react';
import dayjs from 'dayjs';
import {
  FILTER_DATE_FORMAT,
} from '../../../constants/constants';

// Define the structure of the models

export type AurDataGridFilterState = {
  searchState: GridFilterModel;
  sortState: GridSortModel;
  paginationState: GridPaginationModel;
};

type UseServerSideDataGridReturn<Tdata> = {
  totalRowCount: number;
  rowsData: Tdata[];
  isLoading: boolean;
  filterState?: AurDataGridFilterState;
  serverSideEvents: AurDataGridServerSideEvents;
  reloadDataGrid: () => void;
};
export type AurDataGridServerSideEvents = {
  handleFilterChange: (model: GridFilterModel, debounceTimeMS?: number) => void;
  handleSortChange: (model: GridSortModel) => void;
  handlePaginationChange: (model: GridPaginationModel) => void;
  onFilterChange?: (model: GridFilterModel, debounceTimeMS?: number) => void;
};

export type GetTableData<TData> = (
  filterState: AurDataGridFilterState
) => Promise<{
  data: TData[];
  totalCount: number;
}>;

//--------------------------- Hook starts -------------------------------------------
const useServerSideDataGrid = <TData>(
  getData: GetTableData<TData>,
  defaultFilterState: AurDataGridFilterState
): UseServerSideDataGridReturn<TData> => {
  const [filterState, setFilterState] =
    useState<AurDataGridFilterState>(defaultFilterState);
  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    defaultFilterState.searchState
  );
  const [sortModel, setSortModel] = useState<GridSortModel>(
    defaultFilterState.sortState
  );
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    defaultFilterState.paginationState
  );
  const [reload, setReload] = useState(false);
  const reloadDataGrid = () => {
    setReload((pv) => !pv);
  };
  const [totalRowCount, setTotalRowCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rowsData, setRowsData] = useState<TData[]>([]);
  const [fetchDataInitial, ] = useState<boolean>(true);

  const fetchData = useCallback(
    async (filterData: AurDataGridFilterState) => {
      setIsLoading(true);

      const result = await getData(filterData);
      setRowsData(result.data);
      setTotalRowCount(result.totalCount);

      setIsLoading(false);
    },
    [filterModel, sortModel, paginationModel]
  );

  let debounceTimer: NodeJS.Timeout | null = null;
  const handleFilterChange = (model: GridFilterModel, debounceTimeMS = 0) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(() => {
      setFilterModel(model);
      setPaginationModel({...paginationModel, page: 1});
    }, debounceTimeMS);
  };

  const handleSortChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  useEffect(() => {
    setFilterState({
      paginationState: {...paginationModel},
      searchState: {...filterModel},
      sortState: [...sortModel],
    });
    fetchData({
      paginationState: {...paginationModel},
      searchState: {...filterModel},
      sortState: [...sortModel],
    });
  }, [filterModel, sortModel, paginationModel, reload, fetchDataInitial]);

  return {
    totalRowCount: totalRowCount,
    rowsData,
    isLoading,
    filterState,
    serverSideEvents: {
      handleFilterChange,
      handleSortChange,
      handlePaginationChange,
      onFilterChange: handleFilterChange,
    },
    reloadDataGrid: reloadDataGrid,
  };
};

export default useServerSideDataGrid;

export const getFormatedDateForFilter = (
  date: Date | string,
  format: string = FILTER_DATE_FORMAT
): string => {
  return dayjs(date).format(format);
};

export function toODataQueryResult(
  filterModel: GridFilterModel,
  sortModel: GridSortModel,
  paginationModel: GridPaginationModel,
  skipColumnNames?: Array<string>,
  pageName?: string
): string {
  // Convert filter model to OData filters
  let filterQuery = '';
  filterQuery = filterModel?.items
    .map((item) => {
      if (item.value == null) return '';
      if (item.operator === 'contains') {
        return skipColumnNames?.includes(item.field)
          ? `contains(${item.field}, '${item.value}')`
          : `contains(tolower(${item.field}), tolower('${item.value}'))`;
      } else if (item.operator === 'is') {
        return `${item.field} eq ${item.value}`;
      } else if (item.operator === 'before') {
        return `${item.field} lt ${item.value}`;
      } else if (item.operator === 'onOrBefore') {
        return `${item.field} le ${item.value}`;
      } else if (item.operator === 'after') {
        return `${item.field} gt ${item.value}`;
      } else if (item.operator === 'onOrAfter') {
        return `${item.field} ge ${item.value}`;
      } else if (item.operator === 'isAnyOf') {
        return `${item.field} in (${item.value.map((v: string) => `'${v}'`).join(', ')})`;
      } else {
        return `${item.field} ${item.operator} '${item.value}'`;
      }
    })
    .filter(Boolean)
    .join(' and ');

  if (pageName) {
    if (filterQuery) {
      filterQuery = `${filterQuery} and Type eq '${pageName}'`;
    } else {
      filterQuery = `Type eq '${pageName}'`;
    }
  }

  const sortQuery = sortModel
    .map((sortItem) => `${sortItem.field} ${sortItem.sort}`)
    .join(', ');

  const queryParams: string[] = [];
  const top = paginationModel.pageSize;
  const skip = (paginationModel.page - 1) * paginationModel.pageSize;

  if (filterQuery) queryParams.push(`$filter=${filterQuery}`);
  if (sortQuery) queryParams.push(`$orderby=${sortQuery}`);
  queryParams.push(`$top=${top}&$skip=${skip}`);

  const query = queryParams.join('&');
  return query;
}

export const getOperatorFilterValue = (item: GridFilterItem) => {
  // Check if the operator is 'contains' and format accordingly
  if (item.operator === 'contains') {
    return `contains(${item.field}, '${item.value}')`;
  } else if (item.operator === 'is') {
    return `${item.field} eq ${getFormatedDateForFilter(item.value)}`;
  } else if (item.operator === 'before') {
    return `${item.field} lt ${getFormatedDateForFilter(item.value)}`;
  } else if (item.operator === 'onOrBefore') {
    return `${item.field} le ${getFormatedDateForFilter(item.value)}`;
  } else if (item.operator === 'after') {
    return `${item.field} gt ${getFormatedDateForFilter(item.value)}`;
  } else if (item.operator === 'onOrAfter') {
    return `${item.field} ge ${getFormatedDateForFilter(item.value)}`;
  } else {
    return `${item.field} ${item.operator} '${item.value}'`;
  }
};

export function toODataQuery(
  filterModel: GridFilterModel,
  sortModel: GridSortModel,
  paginationModel: GridPaginationModel,
  customFieldFormatter?: {[key: string]: (item: GridFilterItem) => string}
): string {
  // Convert filter model to OData filters
  const filterQuery = filterModel?.items
    .map((item) => {
      // When Not Set is selected then value will not exist but need to send it event in parent using customFieldFormatter
      if (
        customFieldFormatter &&
        customFieldFormatter[item.field] &&
        item.operator === 'NotSet'
      ) {
        return customFieldFormatter[item.field](item);
      }

      if (item.value == null) return '';

      // Check for custom field formatter
      if (customFieldFormatter && customFieldFormatter[item.field]) {
        return customFieldFormatter[item.field](item);
      }
      return getOperatorFilterValue(item);
    })
    .filter(Boolean)
    .join(' and ');

  // Convert pagination model to OData `$top` and `$skip`
  const top = paginationModel.pageSize;
  const skip = (paginationModel.page - 1) * paginationModel.pageSize;

  // Convert sort model to OData `$orderby`
  const sortQuery = sortModel
    .map((sortItem) => `${sortItem.field} ${sortItem.sort}`)
    .join(', ');

  // Construct the final OData query string
  let query = '?';
  if (filterQuery) query += `$filter=${filterQuery}&`;
  if (sortQuery) query += `$orderby=${sortQuery}&`;
  query += `$top=${top}&$skip=${skip}`;

  return query;
}

// added one more function to create ODataQuery with lowercase support
export function toODataQueryLowercase(
  filterModel: GridFilterModel,
  sortModel: GridSortModel,
  paginationModel: GridPaginationModel,
  skipColumnName?: string
): string {
  const filterQuery = filterModel?.items
    .map((item) => {
      if (item.value == null) return '';
      if (item.operator === 'contains') {
        return item.field === skipColumnName
          ? `contains(${item.field}, '${item.value}')`
          : `contains(tolower(${item.field}), tolower('${item.value}'))`;
      } else if (item.operator === 'is') {
        return `${item.field} eq ${item.value}`;
      } else if (item.operator === 'before') {
        return `${item.field} lt ${item.value}`;
      } else if (item.operator === 'onOrBefore') {
        return `${item.field} le ${item.value}`;
      } else if (item.operator === 'after') {
        return `${item.field} gt ${item.value}`;
      } else if (item.operator === 'onOrAfter') {
        return `${item.field} ge ${item.value}`;
      } else if (item.operator === 'isAnyOf') {
        return `${item.field} isAnyOf ${item.value}`;
      } else {
        return `${item.field} ${item.operator} '${item.value}'`;
      }
    })
    .filter(Boolean)
    .join(' and ');

  const top = paginationModel.pageSize;
  const skip = (paginationModel.page - 1) * paginationModel.pageSize;

  const sortQuery = sortModel
    .map((sortItem) => `${sortItem.field} ${sortItem.sort}`)
    .join(', ');

  let query = '?';
  if (filterQuery) query += `$filter=${filterQuery}&`;
  if (sortQuery) query += `$orderby=${sortQuery}&`;
  query += `$top=${top}&$skip=${skip}`;

  return query;
}

export function toODataQueryExport(
  filterModel: GridFilterModel,
  sortModel: GridSortModel,
  paginationModel: GridPaginationModel
): string {
  // Convert filter model to OData filters
  const filterQuery = filterModel?.items
    .map((item) => {
      if (item.value == null) return '';
      // Check if the operator is 'contains' and format accordingly
      if (item.operator === 'contains') {
        return `contains(tolower(${item.field}), tolower('${item.value}'))`;
      } else if (item.operator === 'is') {
        return `${item.field} eq ${getFormatedDateForFilter(item.value)}`;
      } else if (item.operator === 'before') {
        return `${item.field} lt ${getFormatedDateForFilter(item.value)}`;
      } else if (item.operator === 'onOrBefore') {
        return `${item.field} le ${getFormatedDateForFilter(item.value)}`;
      } else if (item.operator === 'after') {
        return `${item.field} gt ${getFormatedDateForFilter(item.value)}`;
      } else if (item.operator === 'onOrAfter') {
        return `${item.field} ge ${getFormatedDateForFilter(item.value)}`;
      } else if (item.operator === 'isAnyOf') {
        return `${item.field} in [${item.value.map((v: string) => `'${v}'`).join(', ')}]`;
      } else {
        return `${item.field} ${item.operator} '${item.value}'`;
      }
    })
    .filter(Boolean)
    .join(' and ');

  // Convert pagination model to OData `$top` and `$skip`
  const top = paginationModel.pageSize;
  const skip = (paginationModel.page - 1) * paginationModel.pageSize;

  // Convert sort model to OData `$orderby`
  const sortQuery = sortModel
    .map((sortItem) => `${sortItem.field} ${sortItem.sort}`)
    .join(', ');

  // Construct the final OData query string
  let query = '';
  if (filterQuery) query += `$filter=${filterQuery}&`;
  if (sortQuery) query += `$orderby=${sortQuery}&`;
  query += `$top=${top}&$skip=${skip}`;

  return query;
}
